import { Vector2 } from "three";
import { ClassFlag } from "../../dom-tools/ClassFlag";
import { DOMHitTest } from "../../dom-tools/DOMHitTest";
import { EventSlot } from "../../events/EventSlot";
import { Property } from "../../events/Property";
import { Dimensions } from "../basics/Dimensions";
import { DragData } from "../basics/DragData";
import { Margins } from "../basics/Margins";
import { UIListProperty } from "../basics/UIListProperty";
import { UIProperty } from "../basics/UIProperty";

export class ElementWithGraphicalElement extends HTMLElement
{
  __graphicalElement:GraphicalElement;
}

export class GraphicalElement
{
  static readonly flag = new ClassFlag( "ui--graphical--element" );

  #element:ElementWithGraphicalElement;


  get element()
  {
    return this.#element;
  }

  get style()
  {
    return this.#element.style;
  }

  
  protected _properties:UIProperty<any>[] = [];
  protected _listProperties:UIListProperty<any>[] = [];

  setFlags( ...flags:ClassFlag[] ):GraphicalElement
  {
    flags.forEach( f => f.set( this.element ) );
    return this;
  }

  protected _setElement( element:Element, classFlag:ClassFlag =  GraphicalElement.flag )
  {
    this.#element = element as ElementWithGraphicalElement;
    this.#element.__graphicalElement = this;   

    if ( ! classFlag )
    {
      return;
    }

    classFlag.set( this.#element );

    this._onElementCreated();

  }

  _addProperty( uiProperty:UIProperty<any> )
  {
    if ( uiProperty.graphicalElement !== this )
    {
      return;
    }

    this._properties.push( uiProperty );
  }

  _addListProperty( uiListProperty:UIListProperty<any> )
  {
    if ( uiListProperty.graphicalElement !== this )
    {
      return;
    }

    this._listProperties.push( uiListProperty );
  }

  protected _onElementCreated()
  {

  }

  readonly dimensions = new Dimensions();
  readonly margins = new UIProperty<Margins>( this, new Margins() );

  get left()
  {
    return this.dimensions.x + this.margins.value.left;
  }

  get top()
  {
    return this.dimensions.y + this.margins.value.top;
  }

  get width()
  {
    return this.dimensions.width - this.margins.value.horizontalSum;
  }

  get height()
  {
    return this.dimensions.height - this.margins.value.verticalSum;
  }

  get size()
  {
    return new Vector2( this.width, this.height );
  }



  #boundRedraw:()=>void;

  get $redraw()
  {
    if ( this.#boundRedraw )
    {
      return this.#boundRedraw
    }

    this.#boundRedraw = this.redraw.bind( this );
    return this.#boundRedraw;
  }


  constructor()
  {
    this._linkRedraw( this.dimensions.onChange, this.margins );    
  }

  _linkRedraw( ...slots:EventSlot<any>[] )
  {
    slots.forEach( s => s.addListener( this.$redraw ) );
  }

  protected _redrawBlock = false;
  
  blockRedraw(){ this._redrawBlock = true; }
  allowRedraw(){ this._redrawBlock = false; }

  forceRedraw()
  {
    this._redrawBlock = false;
    this.redraw();  
  }


  protected _update()
  {
    if ( ! this.element || this._redrawBlock )
    {
      //console.log( "NOT UPDATED", this );
      return;
    }

    //console.log( "UPDATED", this );
    
    this.dimensions.applyTo( this.element, this.margins.value );
  }

  protected _updateChildren()
  {

  }

  redraw()
  {
    if ( this._redrawBlock )
    {
      return;
    }

    this._update();
    this._updateChildren();
  }

  _dragData:DragData<any> = null;
  
  get startPointerPosition()
  {
    return this._dragData.position;
  }

  get dragData()
  {
    return this._dragData;
  }

  getDragData<T>():DragData<T>
  {
    return this._dragData as DragData<T>;
  }

  setDragData( e:TouchEvent|MouseEvent, value?:any )
  {
    this._dragData = DragData.create( e, value );
  }

  clearDragData()
  {
    this._dragData = null;
  }
  
}